import { useState, useContext, useMemo, useEffect } from 'react'
import clsx from 'clsx'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import Lottie from 'lottie-react'
import LoaderAdm from '../../../assets/svgIcons/LoaderAdm.json'

import { HeadingTitle } from '../../../components/modules/ui/index'
import CustomButton from '../../../components/modules/ui/CustomButton'
import Table from './Table'
import {
  currentMonth,
  currentYear,
  monthsOptions as months,
  yearsOptions
} from '../../../components/utils/constant'
import GlobalContext from '../../../context/global-context'

// Hooks
import { useInfiniteMovements } from '../../../hooks/useInfiniteMovements'
import { useInfiniteScroll } from '../../../hooks/useInfiniteScroll'
import FilterControl from '../../../components/modules/table/FilterControl'
import ExportToXlsxModal from '../../../components/modules/modals/ExportToXlsxModal'
import businessApi from '../../../api/business.api'
import { getDateRange } from '../../../utils/date.util'

const ListLibroAuxiliarCliente = () => {
  const { currentBusiness } = useContext(GlobalContext)

  const [filterValues, setFilterValues] = useState({
    desde: '',
    hasta: '',
    descripcion: ''
  })
  const [monthFilter, setMonthFilter] = useState('')
  const [yearFilter, setYearFilter] = useState(currentYear)
  const [documentStatusFilter, setDocumentStatusFilter] = useState('')

  const [showFilters, setShowFilters] = useState(false)
  const [debouncedDescripcion, setDebouncedDescripcion] = useState('')
  const [debouncedDesde, setDebouncedDesde] = useState('')
  const [debouncedHasta, setDebouncedHasta] = useState('')
  const [isExportModalOpen, setIsExportModalOpen] = useState(false)
  const [isExporting, setIsExporting] = useState(false)

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedDescripcion(filterValues.descripcion)
    }, 300)

    return () => clearTimeout(timer)
  }, [filterValues.descripcion])

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedDesde(filterValues.desde)
    }, 300)

    return () => clearTimeout(timer)
  }, [filterValues.desde])

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedHasta(filterValues.hasta)
    }, 300)

    return () => clearTimeout(timer)
  }, [filterValues.hasta])

  const {
    data: infiniteData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    refetch
  } = useInfiniteMovements({
    businessId: currentBusiness?.id || 0,
    month: monthFilter,
    year: yearFilter,
    linkage: true,
    limit: 30,
    filters: {
      type: 'ingreso',
      documentStatus: documentStatusFilter || undefined,
      searchQuery: debouncedDescripcion,
      minAmount: debouncedDesde,
      maxAmount: debouncedHasta
    }
  })

  const { lastElementRef } = useInfiniteScroll({
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage
  })

  const data = useMemo(() => {
    return infiniteData?.pages?.flatMap(page => page.data) || []
  }, [infiniteData])

  const handleFilterChange = (key, value) => {
    setFilterValues(prev => ({ ...prev, [key]: value }))
  }

  const handleClearFilters = () => {
    setFilterValues({
      desde: '',
      hasta: '',
      descripcion: ''
    })
    setShowFilters(false)
    setMonthFilter('')
    setYearFilter(currentYear)
    setDocumentStatusFilter('')
  }

  const estadoOptions = [
    { value: '', label: 'Todos' },
    { value: 'POR COBRAR', label: 'Por cobrar' },
    { value: 'COBRADO', label: 'Cobrado' },
    { value: 'PARCIALMENTE COBRADO', label: 'Parcialmente cobrado' }
  ]

  const monthsOptions = useMemo(() => [
    { value: '', label: 'Todos los meses' },
    ...months.map(opt => ({
      ...opt,
      disabled: yearFilter === currentYear && opt.value > currentMonth
    }))
  ], [yearFilter, currentYear, currentMonth])

  const icons = [
    <button
      key='export-xlsx'
      className='w-10 h-10 text-green-700 font-bold cursor-pointer rounded-full shadow bg-white'
      onClick={() => setIsExportModalOpen(true)}
    >
      XLS
    </button>
  ]

  const dateRange = useMemo(() => getDateRange(yearFilter, monthFilter), [yearFilter, monthFilter])

  const handleExport = async (email) => {
    setIsExporting(true)
    try {
      await businessApi.exportLibroAuxiliar({
        organizationId: currentBusiness.organizationId,
        businessId: currentBusiness.id,
        email,
        from: dateRange.from,
        to: dateRange.to,
        linkage: false,
        type: 'ingreso'
      })
    } catch (error) {
      console.error('Error al exportar:', error)
      throw error
    } finally {
      setIsExporting(false)
    }
  }

  return (
    <div className="w-full px-4">
      <HeadingTitle
        title="Libro Auxiliar de Cliente"
        rightIcons={icons}
        iconHeight={32}
      />

      <div className="mb-4">
        <div className="flex flex-col gap-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3 items-end">
            <FilterControl
              label="Mes"
              value={monthFilter}
              onChange={setMonthFilter}
              options={monthsOptions}
              fullWidth
            />
            <FilterControl
              label="Año"
              value={yearFilter}
              onChange={setYearFilter}
              options={yearsOptions}
              fullWidth
            />
            <FilterControl
              label="Estado"
              value={documentStatusFilter}
              onChange={setDocumentStatusFilter}
              options={estadoOptions}
              fullWidth
            />
            <CustomButton
              className="rounded-full h-8 w-full"
              color="primary"
              title="Buscar"
              onClick={refetch}
              disabled={isLoading}
            />
            <CustomButton
              className="rounded-full h-8 w-full"
              color="accent1"
              title="Limpiar filtros"
              onClick={handleClearFilters}
              disabled={isLoading}
            />
          </div>

          <div className="flex justify-end">
            <button
              className="flex items-center px-2 py-1 bg-surface/70 border border-border rounded hover:bg-border/30 transition"
              onClick={() => setShowFilters(prev => !prev)}
            >
              <KeyboardArrowDownIcon
                className={clsx('transition-transform', {
                  'rotate-180': showFilters,
                })}
              />
            </button>
          </div>
        </div>

        {showFilters && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end border border-border rounded-md p-4 bg-surface/70 mt-4">
            <div className="flex flex-col w-full">
              <label className="text-sm font-medium mb-1">Monto Desde</label>
              <input
                type="number"
                placeholder="Desde"
                value={filterValues.desde}
                onChange={e => handleFilterChange('desde', e.target.value)}
                className="w-full border border-border rounded-full px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
              />
            </div>
            <div className="flex flex-col w-full">
              <label className="text-sm font-medium mb-1">Monto Hasta</label>
              <input
                type="number"
                placeholder="Hasta"
                value={filterValues.hasta}
                onChange={e => handleFilterChange('hasta', e.target.value)}
                className="w-full border border-border rounded-full px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
              />
            </div>

            <div className="flex flex-col w-full">
              <label className="text-sm font-medium mb-1">Descripción</label>
              <input
                type="text"
                placeholder="Buscar por cliente o RUT"
                value={filterValues.descripcion}
                onChange={e => handleFilterChange('descripcion', e.target.value)}
                className="w-full border border-border rounded-full px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
              />
            </div>
          </div>
        )}
      </div>

      {isLoading ? (
        <div className='flex items-center justify-center pt-8 h-full'>
          <Lottie animationData={LoaderAdm} loop style={{ width: 300, height: 300 }} />
        </div>
      ) : data.length === 0 ? (
        <div className="grid place-items-center py-12 text-muted font-sans">
          No hay datos para mostrar con estos filtros
        </div>
      ) : (
        <Table
          rows={data}
          lastElementRef={lastElementRef}
          className="h-[43vh] border border-border rounded bg-surface font-sans text-text"
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          onLoadMore={fetchNextPage}
        />
      )}
      <ExportToXlsxModal
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        onExport={handleExport}
        title='Exportar Libro Auxiliar de Clientes'
        description='Se enviará un archivo Excel con el libro auxiliar de clientes al email especificado'
        isLoading={isExporting}
      />
    </div>
  )
}

export default ListLibroAuxiliarCliente
